/**
 * フロントエンド権限管理
 *
 * ログイン後にAPIから自分の権限を取得し、sessionStorageに保存する。
 * 各コンポーネントから参照して編集UIの表示/非表示を制御する。
 *
 * 「全機能」エントリがある場合、その権限レベルは全機能に適用される。
 *
 * ローカル開発時（AUTH_DISABLED）: ユーザー選択画面でパスワード認証後、
 * そのユーザーの権限を使用する。
 */
import { isAuthDisabled } from './auth';

const PERMISSIONS_KEY = 'mobibi_user_permissions';
const PERMISSIONS_SAVED_AT_KEY = `${PERMISSIONS_KEY}_savedAt`;
// TTL をミリ秒で設定（9時間）
const PERMISSIONS_TTL_MS = 9 * 60 * 60 * 1000;
const ALL_FUNCTIONS = '全機能';
const LOCAL_USER_KEY = 'mobibi_local_user';
const LOCAL_USER_NAME_KEY = 'mobibi_user_name';

/**
 * ローカル開発時にユーザーを選択済みかどうか。
 */
function hasLocalUserSelected() {
	try {
		return Boolean(sessionStorage.getItem(LOCAL_USER_KEY));
	} catch {
		return false;
	}
}

/**
 * 選択中のユーザーの社員番号を返す（未選択なら空文字）。
 *
 * 認証無効環境ではトークンが無く、バックエンドは誰の操作か判別できない。
 * 座席割り当てのように個人単位で動く機能のため、この値をリクエストヘッダーで
 * 送ってバックエンドに本人として扱わせる。
 */
export function getLocalEmployeeId() {
	try {
		return sessionStorage.getItem(LOCAL_USER_KEY) || '';
	} catch {
		return '';
	}
}

/**
 * ローカル開発時のユーザー選択を解除する。
 *
 * 権限は選択時のパスワード認証でしか取得できず、後から取り直す手段がない。
 * 選択だけ残して権限が空になると、全画面が権限ゼロ＝閲覧専用のまま復帰できなく
 * なるため、権限を失うときは選択も一緒に解除してユーザー選択画面に戻す。
 */
export function clearLocalUserSelection() {
	try {
		sessionStorage.removeItem(LOCAL_USER_KEY);
		sessionStorage.removeItem(LOCAL_USER_NAME_KEY);
	} catch {
		/* sessionStorage が使えない環境では何もしない */
	}
}

export function getMyPermissions() {
	try {
		const raw = sessionStorage.getItem(PERMISSIONS_KEY);
		const savedAt = sessionStorage.getItem(PERMISSIONS_SAVED_AT_KEY);
		if (!raw || !savedAt) {
			if (hasLocalUserSelected()) clearLocalUserSelection();
			return [];
		}
		// TTL チェック
		if (Date.now() - Number(savedAt) > PERMISSIONS_TTL_MS) {
			sessionStorage.removeItem(PERMISSIONS_KEY);
			sessionStorage.removeItem(PERMISSIONS_SAVED_AT_KEY);
			clearLocalUserSelection();
			return [];
		}
		return JSON.parse(raw);
	} catch {
		return [];
	}
}

export function setMyPermissions(permissions) {
	sessionStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions));
	sessionStorage.setItem(PERMISSIONS_SAVED_AT_KEY, String(Date.now()));
}

export function clearMyPermissions() {
	sessionStorage.removeItem(PERMISSIONS_KEY);
	sessionStorage.removeItem(PERMISSIONS_SAVED_AT_KEY);
}

/**
 * 指定した機能の権限レベルを取得する。
 * 個別機能のエントリと「全機能」のエントリのうち、高い方を返す。
 */
function getPermissionLevel(functionName) {
	const perms = getMyPermissions();
	const entry = perms.find((p) => p.function === functionName);
	const allEntry = perms.find((p) => p.function === ALL_FUNCTIONS);
	return Math.max(entry?.permission_id || 0, allEntry?.permission_id || 0);
}

/**
 * 指定した機能で閲覧権限を持っているか
 * @param {string} functionName - 例: 'ダッシュボード'
 * @returns {boolean}
 */
export function canView(functionName = 'ダッシュボード') {
	if (isAuthDisabled() && !hasLocalUserSelected()) return true;
	return getPermissionLevel(functionName) >= 1;
}

/**
 * 指定した機能で編集権限を持っているか
 * @param {string} functionName - 例: 'ダッシュボード'
 * @returns {boolean}
 */
export function canEdit(functionName = 'ダッシュボード') {
	if (isAuthDisabled() && !hasLocalUserSelected()) return true;
	return getPermissionLevel(functionName) >= 2;
}

/**
 * 管理者権限（管理者 or 統括管理者）を持っているか
 * permission_id: 3=管理者, 4=統括管理者
 * @param {string} functionName - 例: 'ダッシュボード'
 * @returns {boolean}
 */
export function canAdmin(functionName = 'ダッシュボード') {
	if (isAuthDisabled() && !hasLocalUserSelected()) return true;
	return getPermissionLevel(functionName) >= 3;
}

/**
 * 統括管理者権限を持っているか
 * permission_id: 4=統括管理者
 * @param {string} functionName - 例: 'ダッシュボード'
 * @returns {boolean}
 */
export function canSuperAdmin(functionName = 'ダッシュボード') {
	if (isAuthDisabled() && !hasLocalUserSelected()) return true;
	return getPermissionLevel(functionName) >= 4;
}

/**
 * システム管理者権限を持っているか
 * 「全機能」に対して permission_id=5 が付与されているかで判定する。
 * @returns {boolean}
 */
export function canSystemAdmin() {
	if (isAuthDisabled() && !hasLocalUserSelected()) return true;
	const perms = getMyPermissions();
	const entry = perms.find((p) => p.function === ALL_FUNCTIONS);
	return entry?.permission_id >= 5;
}
